import React, { useMemo } from 'react';
import { MessageSquare, Crosshair, Check, Link2 } from 'lucide-react';
import { hexToRgb } from './utils';

const getCategoryLabel = (category) => {
    if (!category) return '';
    if (category === 'customWall') return 'Custom Wall';
    return category.charAt(0).toUpperCase() + category.slice(1);
};

const renderEdgeName = (key, edge, p1, p2, invScale) => {
    if (!edge?.name) return null;
    const { name, textSize, textColor } = edge;

    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;

    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI;
    let displayAngle = angle;
    if (displayAngle > 90) displayAngle -= 180;
    if (displayAngle < -90) displayAngle += 180;

    return (
        <text
            key={key}
            x={midX}
            y={midY}
            transform={`rotate(${displayAngle} ${midX} ${midY})`}
            dy={-8 * invScale}
            textAnchor="middle"
            dominantBaseline="central"
            fill={textColor || '#047857'}
            fontSize={(textSize || 12) * invScale}
            className="pointer-events-none select-none font-semibold"
        >
            {name}
        </text>
    );
};

export const CanvasRenderer = ({
    isPanning,
    transform,
    pdfPage,
    planOpacity,
    renderableEntities,
    entitiesOpacity,
    unreadCounts,
    handleOpenFeedback,
    entities,
    selectedEntityIds,
    selectedEntity,
    singleSelectedEntityId,
    focusedArea,
    isDark,
    tool,
    canEdit,
    handleEntityClick,
    onContextMenu,
    onImageOrHandleMouseDown,
    onAnchorMouseDown,
    selectedEdgeIndices,
    handleEdgeClick,
    renderPreview,
    displayMode,
    materials,
    furniture,
    decorativeImages,
    handleMaterialSelect,
    handleFurnitureSelect,
    masterStyle,
    showConfirmations,
    onConfirmItem,
    currentUser,
    effectiveIsPreview,
    draggedImageState,
    draggedAnchorState,
}) => {

    const masterImageStyle = masterStyle?.image || {};
    
    const invScale = 1 / (transform.k || 1);

    const sortedRenderableEntities = useMemo(() => 
        [...renderableEntities].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
    , [renderableEntities]);

    const getEntityCentroid = (entity) => {
        if (!entity) return { x: 0, y: 0 };
        switch (entity.type) {
            case 'polygon':
                if (!entity.points || entity.points.length === 0) return { x: 0, y: 0 };
                const total = entity.points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
                return { x: total.x / entity.points.length, y: total.y / entity.points.length };
            case 'rect':
                return { x: entity.x + entity.width / 2, y: entity.y + entity.height / 2 };
            case 'ellipse':
                return { x: entity.cx, y: entity.cy };
            default:
                return { x: 0, y: 0 };
        }
    };
    
    const gStyle = {
        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})`,
        transition: isPanning ? 'none' : 'transform 0.6s ease-in-out',
    };

    const animationStyle = `
        .moodboard-item {
            transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
    `;

    const renderAreaEntities = (isInteractive = true) => (
        sortedRenderableEntities.filter(e => e.type !== 'image').map(ent => {
            const isSelected = selectedEntityIds.includes(ent.id);
            
            let groupOpacity = 1;
            let groupPointerEvents = isInteractive ? 'auto' : 'none';

            if (effectiveIsPreview && focusedArea && focusedArea.id !== ent.id) {
                groupOpacity = 0;
                groupPointerEvents = 'none';
            }

            const defaultFillColor = '#3b82f6';
            const fillOpacity = ent.fillOpacity ?? 0.05;
            const rgb = hexToRgb(ent.fillColor || defaultFillColor);
            const finalFill = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${fillOpacity * entitiesOpacity})`;
            
            const commonProps = {
                stroke: isSelected ? '#f59e0b' : "#3b82f6",
                strokeWidth: (isSelected ? 3 : 2) * invScale,
                fill: finalFill,
                onClick: isInteractive ? (e) => handleEntityClick(e, ent.id) : undefined,
                style: { cursor: tool === 'select' && isInteractive ? 'pointer' : 'default' }
            };

            const centroid = getEntityCentroid(ent);
            const defaultTextColor = isDark ? '#FFFFFF' : '#000000';
            
            return (
                <g key={ent.id} className="group/entity" style={{ opacity: groupOpacity, pointerEvents: groupPointerEvents, transition: 'opacity 0.3s ease-in-out' }}>
                    {ent.type === 'polygon' && <polygon points={ent.points.map(p => `${p.x},${p.y}`).join(' ')} {...commonProps} />}
                    {ent.type === 'rect' && <rect x={ent.x} y={ent.y} width={ent.width} height={ent.height} {...commonProps} />}
                    {ent.type === 'ellipse' && <ellipse cx={ent.cx} cy={ent.cy} rx={ent.rx} ry={ent.ry} {...commonProps} />}
                    {(ent.nameVisible ?? true) && (
                        <text
                            x={centroid.x + (ent.nameOffsetX || 0)}
                            y={centroid.y + (ent.nameOffsetY || 0)}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill={ent.nameTextColor || defaultTextColor}
                            fontSize={(ent.nameTextSize || 14) * invScale}
                            className="pointer-events-none select-none font-medium"
                        >
                            {ent.name}
                        </text>
                    )}
                </g>
            );
        })
    );

    const renderImageAndLinkEntities = (isInteractive = true) => (
        <>
        {sortedRenderableEntities.filter(e => e.type === 'image').map(ent => {
            let entityToRender = ent;
            if (draggedImageState && draggedImageState.entity.id === ent.id) {
                entityToRender = draggedImageState.entity;
            } else if (draggedAnchorState && draggedAnchorState.entity.id === ent.id) {
                entityToRender = draggedAnchorState.entity;
            }

            const { x, y, rotation = 0, opacity = 1, linkedAreaId, purpose, id } = entityToRender;
            const isFocused = focusedArea && (linkedAreaId === focusedArea.id || id === focusedArea.id);
        
            let isInDisplayMode;
            if (purpose === 'decorative') {
                const displayModes = entityToRender.displayIn ?? ['material', 'furniture'];
                isInDisplayMode = displayModes.includes(displayMode);
            } else {
                isInDisplayMode = (purpose === 'material' && displayMode === 'material') || (purpose === 'furniture' && displayMode === 'furniture');
            }
        
            let transformValue, opacityValue;
            let classNameValue = 'group/entity';
        
             if (effectiveIsPreview) {
                classNameValue += ' moodboard-item';
                if (linkedAreaId) {
                    if (isFocused && isInDisplayMode) {
                        transformValue = `translate(${x} ${y}) rotate(${rotation})`;
                        opacityValue = opacity;
                    } else {
                        const area = entities.find(a => a.id === linkedAreaId);
                        const centroid = getEntityCentroid(area);
                        const startX = entityToRender.linkAnchorX ?? centroid.x;
                        const startY = entityToRender.linkAnchorY ?? centroid.y;
                        transformValue = `translate(${startX} ${startY}) rotate(${rotation})`;
                        opacityValue = 0;
                    }
                } else { // Not linked
                    if (focusedArea) {
                        // An area is focused, hide all unlinked items.
                        transformValue = `translate(${x} ${y}) rotate(${rotation})`;
                        opacityValue = 0;
                    } else {
                        // No area is focused. Show unlinked decorative items, hide others.
                        if (purpose === 'decorative') {
                            transformValue = `translate(${x} ${y}) rotate(${rotation})`;
                            opacityValue = opacity;
                        } else {
                            transformValue = `translate(${x} ${y}) rotate(${rotation})`;
                            opacityValue = 0; // Hide unlinked material/furniture boxes
                        }
                    }
                }
            } else { // Not preview mode (edit mode)
                transformValue = `translate(${x} ${y}) rotate(${rotation})`;
                opacityValue = opacity;
            }
        
            const isFullyVisible = opacityValue > 0;
            const hasOptions = (entityToRender.purpose === 'furniture' && entityToRender.furnitureIds?.length > 0) || (entityToRender.purpose === 'material' && entityToRender.materialIds?.length > 0);
            
            const isSelected = selectedEntityIds.includes(entityToRender.id);
            const handleSize = 8 * invScale;
            const unreadCount = unreadCounts[entityToRender.id];
            const isCustomer = currentUser?.role === 'customer';

            let imageUrl = entityToRender.imageUrl;
            let color = entityToRender.color;
            let name = entityToRender.name;

            if (entityToRender.purpose === 'material' && entityToRender.materialIds && materials) {
                const activeIndex = entityToRender.activeMaterialIndex || 0;
                const activeMaterialId = entityToRender.materialIds[activeIndex];
                const activeMaterial = materials.find(m => m.id === activeMaterialId);
                if (activeMaterial) {
                    imageUrl = activeMaterial.textureUrl || null;
                    color = activeMaterial.color;
                    name = activeMaterial.name;
                }
            } else if (entityToRender.purpose === 'furniture' && entityToRender.furnitureIds && furniture) {
                const activeIndex = entityToRender.activeFurnitureIndex || 0;
                const activeFurnitureId = entityToRender.furnitureIds[activeIndex];
                const activeFurniture = furniture.find(f => f.id === activeFurnitureId);
                if (activeFurniture) {
                    imageUrl = activeFurniture.imageUrl || null;
                    name = activeFurniture.name;
                }
            } else if (entityToRender.purpose === 'decorative' && entityToRender.decorativeImageId && decorativeImages) {
                const decorativeImage = decorativeImages.find(i => i.id === entityToRender.decorativeImageId);
                if (decorativeImage) {
                    imageUrl = decorativeImage.imageUrl || null;
                    name = decorativeImage.name;
                }
            }
            
            const imageRectStyle = {
                imageRendering: 'pixelated',
                cursor: tool === 'select' ? (canEdit ? 'move' : 'pointer') : 'default',
                pointerEvents: isInteractive && isFullyVisible ? 'all' : 'none'
            };

            const messageIconBaseStyle = {
                cursor: 'pointer',
                pointerEvents: isInteractive && isFullyVisible ? 'all' : 'none',
            };
        
            const messageIconClassName = `transition-opacity ${
                unreadCount > 0
                    ? 'opacity-100'
                    : 'opacity-0 group-hover/entity:opacity-100'
            }`;
            
            let isConfirmed = false;
            if (entityToRender.purpose === 'furniture') {
                const activeIndex = entityToRender.activeFurnitureIndex || 0;
                const activeFurnitureId = entityToRender.furnitureIds?.[activeIndex];
                isConfirmed = !!activeFurnitureId && entityToRender.confirmedFurniture?.furnitureId === activeFurnitureId;
            } else if (entityToRender.purpose === 'material' && entityToRender.linkedAreaId) {
                const area = entities.find(a => a.id === entityToRender.linkedAreaId);
                if (area) {
                    const activeMaterialId = entityToRender.materialIds?.[entityToRender.activeMaterialIndex || 0];
                    if (entityToRender.materialCategory === 'customWall') {
                        const edge = area.edges?.[entityToRender.edgeIndex];
                        isConfirmed = edge?.confirmedWallMaterial?.materialId === activeMaterialId;
                    } else if (entityToRender.materialCategory === 'floor' && entityToRender.floorSlotKey && area.floorSlots) {
                        const slot = area.floorSlots.find(s => s.key === entityToRender.floorSlotKey);
                        isConfirmed = slot?.confirmedMaterial?.materialId === activeMaterialId;
                    } else if (entityToRender.materialCategory === 'wall' && entityToRender.wallSlotKey && area.wallSlots) {
                        const slot = area.wallSlots.find(s => s.key === entityToRender.wallSlotKey);
                        isConfirmed = slot?.confirmedMaterial?.materialId === activeMaterialId;
                    } else {
                        isConfirmed = area.confirmedMaterials?.[entityToRender.materialCategory]?.materialId === activeMaterialId;
                    }
                }
            }
            
            return (
                 <React.Fragment key={ent.id}>
                    {entityToRender.linkedAreaId && (entityToRender.isLinkVisible ?? true) && (
                        <g style={{ pointerEvents: 'none', transition: 'opacity 0.3s ease-in-out 0.3s', opacity: isFullyVisible ? 1 : 0 }}>
                            {(() => {
                                const areaEnt = entities.find(a => a.id === entityToRender.linkedAreaId);
                                if (!areaEnt) return null;
                                
                                const anchorX = entityToRender.linkAnchorX ?? getEntityCentroid(areaEnt).x;
                                const anchorY = entityToRender.linkAnchorY ?? getEntityCentroid(areaEnt).y;
                                const defaultLinkColor = isDark ? '#FFFFFF' : '#000000';
    
                                return (
                                    <line
                                        x1={entityToRender.x} y1={entityToRender.y}
                                        x2={anchorX} y2={anchorY}
                                        stroke={entityToRender.linkColor ?? masterImageStyle.linkColor ?? defaultLinkColor}
                                        strokeWidth={(entityToRender.linkWidth ?? masterImageStyle.linkWidth ?? 2) * invScale}
                                        strokeDasharray="5,5"
                                    />
                                )
                            })()}
                        </g>
                    )}
                    <g
                        className={classNameValue}
                        transform={transformValue}
                        opacity={opacityValue}
                        onContextMenu={isInteractive ? (e) => onContextMenu(e, ent.id) : undefined}
                    >
                        {entityToRender.purpose === 'material' && entityToRender.materialCategory && (
                            <text
                                x={0}
                                y={-entityToRender.height / 2 - 12 * invScale}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fill={isDark ? '#a0a0a0' : '#6b7280'}
                                fontSize={10 * invScale}
                                className="pointer-events-none select-none font-semibold uppercase tracking-wider"
                            >
                                {entityToRender.slotName || getCategoryLabel(entityToRender.materialCategory)}
                            </text>
                        )}
                        {(entityToRender.borderWidth ?? masterImageStyle.borderWidth ?? 0) > 0 && (
                            <rect
                                x={-entityToRender.width / 2 - (entityToRender.borderWidth ?? masterImageStyle.borderWidth)}
                                y={-entityToRender.height / 2 - (entityToRender.borderWidth ?? masterImageStyle.borderWidth)}
                                width={entityToRender.width + ((entityToRender.borderWidth ?? masterImageStyle.borderWidth) * 2)}
                                height={entityToRender.height + ((entityToRender.borderWidth ?? masterImageStyle.borderWidth) * 2)}
                                rx={((entityToRender.borderRadius ?? masterImageStyle.borderRadius ?? 0)) + ((entityToRender.borderWidth ?? masterImageStyle.borderWidth) || 0)}
                                ry={((entityToRender.borderRadius ?? masterImageStyle.borderRadius ?? 0)) + ((entityToRender.borderWidth ?? masterImageStyle.borderWidth) || 0)}
                                fill={entityToRender.borderColor ?? masterImageStyle.borderColor ?? '#000000'}
                                className="pointer-events-none"
                            />
                        )}
                        {imageUrl ? (
                            <image
                                href={imageUrl}
                                x={-entityToRender.width / 2}
                                y={-entityToRender.height / 2}
                                width={entityToRender.width}
                                height={entityToRender.height}
                                preserveAspectRatio="none"
                                clipPath={`url(#clip-${ent.id})`}
                                style={imageRectStyle}
                                onClick={isInteractive ? (e) => handleEntityClick(e, ent.id) : undefined}
                                onMouseDown={isInteractive ? (e) => onImageOrHandleMouseDown(e, ent.id, 'move') : undefined}
                            />
                        ) : (
                            <rect
                                x={-entityToRender.width / 2}
                                y={-entityToRender.height / 2}
                                width={entityToRender.width}
                                height={entityToRender.height}
                                fill={color || '#cccccc'}
                                clipPath={`url(#clip-${ent.id})`}
                                style={imageRectStyle}
                                onClick={isInteractive ? (e) => handleEntityClick(e, ent.id) : undefined}
                                onMouseDown={isInteractive ? (e) => onImageOrHandleMouseDown(e, ent.id, 'move') : undefined}
                            />
                        )}

                        {(entityToRender.colorOverlayEnabled ?? masterImageStyle.colorOverlayEnabled) && (
                            <rect
                                x={-entityToRender.width / 2} y={-entityToRender.height / 2}
                                width={entityToRender.width} height={entityToRender.height}
                                fill={entityToRender.colorOverlay ?? masterImageStyle.colorOverlay}
                                fillOpacity={entityToRender.colorOverlayOpacity ?? masterImageStyle.colorOverlayOpacity} className="pointer-events-none"
                                clipPath={`url(#clip-${ent.id})`}
                            />
                        )}
                        {showConfirmations && isConfirmed && (
                            <rect
                                x={-entityToRender.width / 2} y={-entityToRender.height / 2}
                                width={entityToRender.width} height={entityToRender.height}
                                fill="rgba(74, 222, 128, 0.3)"
                                className="pointer-events-none transition-opacity duration-500 ease-in-out"
                                clipPath={`url(#clip-${ent.id})`}
                            />
                        )}
                        {isSelected && !effectiveIsPreview && (
                            <rect
                                x={-entityToRender.width / 2} y={-entityToRender.height / 2}
                                width={entityToRender.width} height={entityToRender.height}
                                fill="none"
                                stroke="#f59e0b"
                                strokeWidth={3 * invScale}
                                rx={entityToRender.borderRadius ?? masterImageStyle.borderRadius ?? 0}
                                ry={entityToRender.borderRadius ?? masterImageStyle.borderRadius ?? 0}
                                className="pointer-events-none"
                            />
                        )}
                        <g
                            transform={`translate(${entityToRender.width / 2 - 15 * invScale}, ${-entityToRender.height / 2 + 15 * invScale})`}
                            style={messageIconBaseStyle}
                            className={messageIconClassName}
                            onClick={(e) => { e.stopPropagation(); handleOpenFeedback(ent.id, 'General'); }}
                        >
                            <circle r={15 * invScale} fill="rgba(30, 41, 59, 0.8)" />
                            <MessageSquare
                                x={-9 * invScale}
                                y={-9 * invScale}
                                width={18 * invScale}
                                height={18 * invScale}
                                stroke="white"
                                strokeWidth={1.5}
                            />
                            {unreadCount > 0 && (
                                <g transform={`translate(${10 * invScale}, ${-10 * invScale})`}>
                                    <circle r={8 * invScale} fill="red" />
                                    <text
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fontSize={10 * invScale}
                                        fill="white"
                                        className="font-bold select-none"
                                    >
                                        {unreadCount}
                                    </text>
                                </g>
                            )}
                        </g>
                        {entityToRender.purpose === 'material' && entityToRender.materialIds && entityToRender.materialIds.length > 1 && (
                            <g transform={`translate(0, ${entityToRender.height / 2 + 17 * invScale})`}>
                                <rect
                                    x={-(entityToRender.materialIds.length * 14 * invScale) / 2}
                                    y={-7 * invScale}
                                    width={entityToRender.materialIds.length * 14 * invScale}
                                    height={14 * invScale}
                                    rx={7 * invScale}
                                    fill="rgba(30, 41, 59, 0.8)"
                                />
                                {entityToRender.materialIds.map((materialId, index) => {
                                    const isActive = index === (entityToRender.activeMaterialIndex || 0);
                                    return (
                                        <circle
                                            key={index}
                                            cx={-(entityToRender.materialIds.length * 14 * invScale) / 2 + (index * 14 + 7) * invScale}
                                            cy={0}
                                            r={5 * invScale}
                                            fill={isActive ? '#f59e0b' : 'white'}
                                            stroke={isActive ? 'none' : 'rgba(30, 41, 59, 0.8)'}
                                            strokeWidth={0.5 * invScale}
                                            style={{ cursor: 'pointer', pointerEvents: isInteractive && isFullyVisible ? 'all' : 'none' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMaterialSelect(ent.id, index);
                                            }}
                                        />
                                    );
                                })}
                            </g>
                        )}
                        {entityToRender.purpose === 'furniture' && entityToRender.furnitureIds && entityToRender.furnitureIds.length > 1 && (
                            <g transform={`translate(0, ${entityToRender.height / 2 + 17 * invScale})`}>
                                <rect
                                    x={-(entityToRender.furnitureIds.length * 14 * invScale) / 2}
                                    y={-7 * invScale}
                                    width={entityToRender.furnitureIds.length * 14 * invScale}
                                    height={14 * invScale}
                                    rx={7 * invScale}
                                    fill="rgba(30, 41, 59, 0.8)"
                                />
                                {entityToRender.furnitureIds.map((furnitureId, index) => {
                                    const isActive = index === (entityToRender.activeFurnitureIndex || 0);
                                    return (
                                        <circle
                                            key={index}
                                            cx={-(entityToRender.furnitureIds.length * 14 * invScale) / 2 + (index * 14 + 7) * invScale}
                                            cy={0}
                                            r={5 * invScale}
                                            fill={isActive ? '#f59e0b' : 'white'}
                                            stroke={isActive ? 'none' : 'rgba(30, 41, 59, 0.8)'}
                                            strokeWidth={0.5 * invScale}
                                            style={{ cursor: 'pointer', pointerEvents: isInteractive && isFullyVisible ? 'all' : 'none' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFurnitureSelect(ent.id, index);
                                            }}
                                        />
                                    );
                                })}
                            </g>
                        )}
                        {effectiveIsPreview && isCustomer && hasOptions && (
                            <g
                                transform={`translate(${entityToRender.width / 2 + 15 * invScale}, 0)`}
                                style={{ cursor: 'pointer', pointerEvents: isFullyVisible ? 'all' : 'none' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (entityToRender.purpose === 'furniture') {
                                        onConfirmItem(ent.id, 'furniture');
                                    } else if (entityToRender.purpose === 'material' && entityToRender.linkedAreaId) {
                                        onConfirmItem(ent.linkedAreaId, entityToRender.materialCategory, { 
                                            edgeIndex: entityToRender.edgeIndex, 
                                            materialId: entityToRender.materialIds?.[entityToRender.activeMaterialIndex || 0],
                                            floorSlotKey: entityToRender.floorSlotKey,
                                            wallSlotKey: entityToRender.wallSlotKey,
                                        });
                                    }
                                }}
                            >
                                <circle r={12 * invScale} fill={isConfirmed ? "rgb(34, 197, 94)" : "rgba(30, 41, 59, 0.8)"} />
                                <Check
                                    x={-8 * invScale} y={-8 * invScale}
                                    width={16 * invScale} height={16 * invScale}
                                    stroke="white" strokeWidth={2}
                                />
                            </g>
                        )}
                        {selectedEntity && selectedEntity.id === entityToRender.id && canEdit && (
                            <>
                                <line
                                    x1={0} y1={-entityToRender.height / 2}
                                    x2={0} y2={-entityToRender.height / 2 - 20 * invScale}
                                    stroke="#f59e0b" strokeWidth={2 * invScale} className="pointer-events-none"
                                />
                                <circle
                                    cx={0}
                                    cy={-entityToRender.height / 2 - 20 * invScale}
                                    r={5 * invScale}
                                    fill="#f59e0b"
                                    style={{cursor: 'alias', pointerEvents: isInteractive ? 'all' : 'none'}}
                                    onMouseDown={(e) => onImageOrHandleMouseDown(e, ent.id, 'rotate')}
                                />
                                {[
                                    { x: -entityToRender.width / 2, y: -entityToRender.height / 2, cursor: 'nwse-resize', action: 'nw-resize' },
                                    { x: 0, y: -entityToRender.height / 2, cursor: 'ns-resize', action: 'n-resize' },
                                    { x: entityToRender.width / 2, y: -entityToRender.height / 2, cursor: 'nesw-resize', action: 'ne-resize' },
                                    { x: entityToRender.width / 2, y: 0, cursor: 'ew-resize', action: 'e-resize' },
                                    { x: entityToRender.width / 2, y: entityToRender.height / 2, cursor: 'nwse-resize', action: 'se-resize' },
                                    { x: 0, y: entityToRender.height / 2, cursor: 'ns-resize', action: 's-resize' },
                                    { x: -entityToRender.width / 2, y: entityToRender.height / 2, cursor: 'nesw-resize', action: 'sw-resize' },
                                    { x: -entityToRender.width / 2, y: 0, cursor: 'ew-resize', action: 'w-resize' },
                                ].map(handle => (
                                    <rect
                                        key={handle.action}
                                        x={handle.x - handleSize / 2}
                                        y={handle.y - handleSize / 2}
                                        width={handleSize}
                                        height={handleSize}
                                        fill="#f59e0b"
                                        stroke="#fff"
                                        strokeWidth={1 * invScale}
                                        style={{cursor: handle.cursor, pointerEvents: isInteractive ? 'all' : 'none'}}
                                        onMouseDown={(e) => onImageOrHandleMouseDown(e, ent.id, handle.action)}
                                    />
                                ))}
                            </>
                        )}
                    </g>
                </React.Fragment>
            )
        })}
        </>
    );

    return (
        <svg 
            width="100%"
            height="100%"
            className="absolute top-0 left-0 z-10"
            style={{ pointerEvents: 'none' }}
        >
            <defs>
                 <style>{animationStyle}</style>
                {sortedRenderableEntities.filter(e => e.type === 'image').map(ent => (
                    <clipPath key={`clip-def-${ent.id}`} id={`clip-${ent.id}`}>
                        <rect
                            x={-ent.width / 2}
                            y={-ent.height / 2}
                            width={ent.width}
                            height={ent.height}
                            rx={ent.borderRadius ?? masterImageStyle.borderRadius ?? 0}
                            ry={ent.borderRadius ?? masterImageStyle.borderRadius ?? 0}
                        />
                    </clipPath>
                ))}
                 {focusedArea && ['polygon', 'rect', 'ellipse'].includes(focusedArea.type) && (
                    <clipPath id="pdf-clip-path">
                        {focusedArea.type === 'polygon' && <polygon points={focusedArea.points.map(p => `${p.x},${p.y}`).join(' ')} />}
                        {focusedArea.type === 'rect' && <rect x={focusedArea.x} y={focusedArea.y} width={focusedArea.width} height={focusedArea.height} />}
                        {focusedArea.type === 'ellipse' && <ellipse cx={focusedArea.cx} cy={focusedArea.cy} rx={focusedArea.rx} ry={focusedArea.ry} />}
                    </clipPath>
                )}
            </defs>
            <g style={gStyle}>
                {pdfPage && (
                    <g opacity={focusedArea ? 0 : 1} style={{transition: 'opacity 0.6s ease-in-out', pointerEvents: focusedArea ? 'none' : 'auto'}}>
                        <image
                            href={pdfPage.src}
                            x="0"
                            y="0"
                            width={pdfPage.width}
                            height={pdfPage.height}
                            style={{ opacity: planOpacity, pointerEvents: 'none' }}
                        />
                    </g>
                )}

                {focusedArea && pdfPage && (
                    <g opacity={focusedArea ? 1 : 0} style={{transition: 'opacity 0.6s ease-in-out', pointerEvents: focusedArea ? 'auto' : 'none'}}>
                         <g clipPath="url(#pdf-clip-path)">
                            <image
                                href={pdfPage.src}
                                x="0"
                                y="0"
                                width={pdfPage.width}
                                height={pdfPage.height}
                                style={{ opacity: planOpacity }}
                            />
                        </g>
                    </g>
                )}

                <g style={{pointerEvents: tool === 'select' ? 'all' : 'none'}}>
                    {renderAreaEntities(true)}
                </g>
                <g style={{pointerEvents: 'auto'}}>
                    {renderImageAndLinkEntities(true)}
                </g>
                
                 {singleSelectedEntityId && ['polygon', 'rect', 'ellipse'].includes(entities.find(e => e.id === singleSelectedEntityId)?.type) && tool === 'select' && (
                    <g style={{pointerEvents: 'auto'}}>
                        {(() => {
                            const selectedArea = entities.find(e => e.id === singleSelectedEntityId);
                            if (!selectedArea) return null;

                            if (selectedArea.type === 'polygon') {
                                return selectedArea.points.map((p1, i) => {
                                    const p2 = selectedArea.points[(i + 1) % selectedArea.points.length];
                                    const isEdgeSelected = selectedEdgeIndices.includes(i);
                                    const edgeHasCustomMaterial = selectedArea.edges[i]?.wallMaterials?.length > 0;

                                    let visualProps;
                                    if (isEdgeSelected) {
                                        visualProps = { stroke: '#f59e0b', strokeOpacity: 1, strokeWidth: 6 * invScale };
                                    } else if (edgeHasCustomMaterial) {
                                        visualProps = { stroke: '#a855f7', strokeOpacity: 0.9, strokeWidth: 4 * invScale };
                                    } else {
                                        visualProps = { stroke: 'transparent', strokeOpacity: 0, strokeWidth: 8 * invScale };
                                    }

                                    return <line key={`edge-${i}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} className="cursor-pointer" onClick={(e) => handleEdgeClick(e, i)} {...visualProps} />;
                                });
                            }
                            if (selectedArea.type === 'rect') {
                                return [
                                    { p1: { x: selectedArea.x, y: selectedArea.y }, p2: { x: selectedArea.x + selectedArea.width, y: selectedArea.y } },
                                    { p1: { x: selectedArea.x + selectedArea.width, y: selectedArea.y }, p2: { x: selectedArea.x + selectedArea.width, y: selectedArea.y + selectedArea.height } },
                                    { p1: { x: selectedArea.x + selectedArea.width, y: selectedArea.y + selectedArea.height }, p2: { x: selectedArea.x, y: selectedArea.y + selectedArea.height } },
                                    { p1: { x: selectedArea.x, y: selectedArea.y + selectedArea.height }, p2: { x: selectedArea.x, y: selectedArea.y } }
                                ].map((edge, i) => {
                                     const isEdgeSelected = selectedEdgeIndices.includes(i);
                                     const edgeHasCustomMaterial = selectedArea.edges[i]?.wallMaterials?.length > 0;
 
                                     let visualProps;
                                     if (isEdgeSelected) {
                                         visualProps = { stroke: '#f59e0b', strokeOpacity: 1, strokeWidth: 6 * invScale };
                                     } else if (edgeHasCustomMaterial) {
                                         visualProps = { stroke: '#a855f7', strokeOpacity: 0.9, strokeWidth: 4 * invScale };
                                     } else {
                                         visualProps = { stroke: 'transparent', strokeOpacity: 0, strokeWidth: 8 * invScale };
                                     }
                                    return <line key={`edge-rect-${i}`} x1={edge.p1.x} y1={edge.p1.y} x2={edge.p2.x} y2={edge.p2.y} className="cursor-pointer" onClick={(e) => handleEdgeClick(e, i)} {...visualProps} />;
                                });
                            }
                            if (selectedArea.type === 'ellipse') {
                                const isEdgeSelected = selectedEdgeIndices.includes(0);
                                const edgeHasCustomMaterial = selectedArea.edges[0]?.wallMaterials?.length > 0;
                                            
                                let visualProps;
                                if (isEdgeSelected) {
                                    visualProps = { stroke: '#f59e0b', strokeOpacity: 1, strokeWidth: 6 * invScale };
                                } else if (edgeHasCustomMaterial) {
                                    visualProps = { stroke: '#a855f7', strokeOpacity: 0.9, strokeWidth: 4 * invScale };
                                } else {
                                    visualProps = { stroke: 'transparent', strokeOpacity: 0, strokeWidth: 8 * invScale };
                                }

                                return <ellipse key="edge-ellipse" cx={selectedArea.cx} cy={selectedArea.cy} rx={selectedArea.rx} ry={selectedArea.ry} fill="none" className="cursor-pointer" onClick={(e) => handleEdgeClick(e, 0)} {...visualProps} />;
                            }
                        })()}
                        {entities.filter(e => e.edges).map(ent => (
                            <g key={`${ent.id}-edge-names`}>
                                {ent.type === 'polygon' && ent.points.map((p1, i) => {
                                    const edge = ent.edges[i];
                                    if (!edge?.name) return null;
                                    const p2 = ent.points[(i + 1) % ent.points.length];
                                    return renderEdgeName(`${ent.id}-edge-text-${i}`, edge, p1, p2, invScale);
                                })}
                                {ent.type === 'rect' && [
                                    { p1: {x: ent.x, y: ent.y}, p2: {x: ent.x + ent.width, y: ent.y} },
                                    { p1: {x: ent.x + ent.width, y: ent.y}, p2: {x: ent.x + ent.width, y: ent.y + ent.height} },
                                    { p1: {x: ent.x + ent.width, y: ent.y + ent.height}, p2: {x: ent.x, y: ent.y + ent.height} },
                                    { p1: {x: ent.x, y: ent.y + ent.height}, p2: {x: ent.x, y: ent.y} }
                                ].map((edgePoints, i) => {
                                    const edge = ent.edges[i];
                                    if (!edge?.name) return null;
                                    return renderEdgeName(`${ent.id}-edge-text-${i}`, edge, edgePoints.p1, edgePoints.p2, invScale);
                                })}
                                {ent.type === 'ellipse' && ent.edges[0]?.name && (
                                    <text
                                        key={`${ent.id}-edge-text-0`}
                                        x={ent.cx}
                                        y={ent.cy - ent.ry - (8 * invScale)}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fill={ent.edges[0].textColor || '#047857'}
                                        fontSize={(ent.edges[0].textSize || 12) * invScale}
                                        className="pointer-events-none select-none font-semibold"
                                    >
                                        {ent.edges[0].name}
                                    </text>
                                )}
                            </g>
                        ))}
                    </g>
                )}
                {renderPreview()}
                
                {selectedEntity && selectedEntity.type === 'image' && selectedEntity.linkedAreaId && canEdit && !effectiveIsPreview && (
                    <g style={{pointerEvents: 'auto'}}>
                        {(() => {
                            const areaEnt = entities.find(a => a.id === selectedEntity.linkedAreaId);
                            if (!areaEnt) return null;

                            let entityToRender = selectedEntity;
                            if (draggedAnchorState && draggedAnchorState.entity.id === selectedEntity.id) {
                                entityToRender = draggedAnchorState.entity;
                            }
                            
                            const anchorX = entityToRender.linkAnchorX ?? getEntityCentroid(areaEnt).x;
                            const anchorY = entityToRender.linkAnchorY ?? getEntityCentroid(areaEnt).y;

                            return (
                                <g>
                                    <circle
                                        cx={anchorX}
                                        cy={anchorY}
                                        r={8 * invScale}
                                        fill="rgba(255, 255, 255, 0.2)"
                                        stroke="#f59e0b"
                                        strokeWidth={2 * invScale}
                                        style={{ cursor: 'move' }}
                                        onMouseDown={(e) => onAnchorMouseDown(e, selectedEntity.id)}
                                    />
                                    <Crosshair
                                        x={anchorX - (6 * invScale)}
                                        y={anchorY - (6 * invScale)}
                                        width={12 * invScale}
                                        height={12 * invScale}
                                        stroke="#f59e0b"
                                        strokeWidth={1.5 * invScale}
                                        className="pointer-events-none"
                                    />
                                </g>
                            );
                        })()}
                    </g>
                )}
            </g>
        </svg>
    );
};
